<template>
  <q-dialog v-model="visible" full-height position="right">
    <q-card style="width: 800px; max-width: 90vw">
      <q-toolbar>
        <q-toolbar-title> {{ $t('label.users.activity_log') }} </q-toolbar-title>
        <q-btn flat round dense icon="close" @click="onCancel" />
      </q-toolbar>
      <q-card-section>
        <div class="max-height-dialog">
          <CLoading :loading="loading" />
          <ActivityLog :userActivityLogs="userData" :last-activity="lastActivity" />
          <div class="text-center" v-if="userData && currentPage < totalPages">
            <q-btn color="grey" :label="$t('label.users.load_more')" rounded @click="loadMore" size="xs" no-caps />
          </div>
        </div>
        <div class="row no-wrap items-center justify-end q-mt-md">
          <div class="q-gutter-sm">
            <q-btn no-caps flat class="btn-cancel" @click="onCancel">{{ $t('close') }}</q-btn>
          </div>
        </div>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component'
import CLoading from '@/components/common/ui/CLoading.vue'
import { maska } from 'maska'
import { Prop } from 'vue-property-decorator'
import ActivityLog from '@/components/app/ActivityLog.vue'
import { ACTION_ACTIVITY_LOG } from '@/store/actions'
import { DatetimeUtils } from '@/utils/datetime'
import { IActivityLog, IDateRangePicker } from '@/utils/types'

@Options({
  components: { ActivityLog, CLoading },
  directives: { maska },
  emits: ['update:closeModal'],
})

export default class AudienceActivityModal extends Vue {
  @Prop()
  modalVisible!: boolean

  @Prop()
  selectedUserId!: string

  @Prop()
  activityDateRange!: IDateRangePicker

  @Prop()
  isTestMode!: boolean

  loading = true
  rawData: IActivityLog[] = []
  userData: {}[] = []
  lastActivity: {} = {}
  currentPage = 1
  totalPages = 0

  get visible() {
    return this.modalVisible
  }

  set visible(value) {
    this.$emit('update:closeModal', value)
  }

  get selectedAppId() {
    return this.$route.params.app_id as string
  }

  async loadInitials(newPage = false) {
    this.loading = true

    const activityLogData = await this.$store.dispatch(ACTION_ACTIVITY_LOG.LOAD_ITEMS, {
      app_id: this.selectedAppId,
      user_id: this.selectedUserId,
      date_from: DatetimeUtils.formatDate(this.activityDateRange.startDate ?? ''),
      date_to: DatetimeUtils.formatDate(this.activityDateRange.endDate ?? ''),
      is_test_mode: this.isTestMode,
      filter: {
        page: this.currentPage,
        per_page: 50,
        hide_events: [],
      },
    })

    if (this.totalPages !== activityLogData.total_pages) {
      this.totalPages = activityLogData.total_pages
    }

    for (const item of activityLogData.items) {
      item.date = item.created_at.split(' ')[0]
      item.time = item.created_at.split(' ')[1]
      item.event_data = [
        {
          _id: item._id,
          name: item.event_type,
          value: item.event_value,
          resource_id: item.resource_id,
          is_test_mode: item.is_test_mode,
          resource_type: item.resource_type,
          url: '',
          loading: false,
        },
      ]
    }

    if (newPage) {
      this.rawData = this.rawData.concat(activityLogData.items)
    } else {
      this.rawData = activityLogData.items
    }
    this.lastActivity = this.rawData[0]
    this.userData = this.buildData(this.rawData)

    this.loading = false
  }

  loadMore() {
    this.currentPage++
    this.loadInitials(true)
  }

  groupBy(list, key) {
    return list.reduce(function (rv, x) {
      ;(rv[x[key]] = rv[x[key]] || []).push(x)
      return rv
    }, {})
  }

  buildData(data) {
    const mergedCampaign = data.reduce((r, { date, campaign_type, campaign_name, ...rest }) => {
      const key = `${date}-${campaign_type}-${campaign_name}`
      r[key] = r[key] || { date, campaign_type, campaign_name, events: [] }
      r[key].events.push(rest)
      return r
    }, {})

    const merged = this.groupBy(Object.values(mergedCampaign), 'date')
    return Object.entries(merged)
  }

  onCancel() {
    this.visible = false
  }

  async created() {
    await this.loadInitials()
  }
}
</script>

<style scoped lang="scss">

</style>
